"use client";

import { Message } from "@/lib/core/crm/types";
import { Client } from "@/lib/types/database";
import { EmailRecipient } from "@/components/crm/messages/ClientEmailSelector";
import {
  ComposeDraft,
  ComposeFormValues,
  FollowUpTaskMessageData,
  EmailConnection,
} from "@/lib/types/compose-modal-types";

// Pure helper lives in a server/client-shared module so the Meridian server
// email-delivery path can call it without the client/server boundary error.
// Re-exported here so existing client callers keep importing it from this file.
export { appendSignatureToBody } from "./signature-utils";

/**
 * Extract draft identifier from message, recipient, or form value
 */
export function extractDraftIdentifier(
  originalMessage?: Message | null,
  initialRecipient?: string,
  watchedTo?: string
): string {
  if (originalMessage) {
    return originalMessage.id;
  }
  return initialRecipient || watchedTo || "";
}

/**
 * Build draft object from form values
 */
export function buildDraftFromFormValues(
  values: ComposeFormValues,
  selectedClient?: Client | null
): ComposeDraft {
  return {
    to: values.to || "",
    subject: values.subject || "",
    body: values.body || "",
    fromAccount: values.fromAccount || "",
    selectedClientId: selectedClient?.id,
    variableValues: {},
  };
}

/**
 * Check if draft has meaningful content
 */
export function hasMeaningfulDraftContent(draft: ComposeDraft): boolean {
  return !!(draft.to || draft.subject || draft.body);
}

/**
 * Build initial subject for reply/forward
 */
export function buildInitialSubject(
  isForwardMode: boolean,
  message?: Message | null
): string | undefined {
  if (!message) return undefined;

  if (isForwardMode && message.subject) {
    return `Fwd: ${message.subject}`;
  }

  if (message.subject) {
    return `Re: ${message.subject}`;
  }

  return undefined;
}

/**
 * Build initial body for reply/forward with quoted text
 */
export function buildInitialBody(
  isForwardMode: boolean,
  message?: Message | null
): string | undefined {
  if (!message) return undefined;

  if (isForwardMode) {
    return `\n\n---------- Forwarded message ----------\nFrom: ${
      message.sender?.name || message.sender?.email
    }\nDate: ${new Date(message.receivedDateTime).toLocaleString()}\nSubject: ${
      message.subject
    }\nTo: ${
      message.recipients?.map((r) => r.email || r.name || "").join(", ") || ""
    }\n\n${message.body || ""}`;
  }

  return `\n\n--- Original Message ---\nFrom: ${
    message.sender?.name || message.sender?.email
  }\nDate: ${new Date(message.receivedDateTime).toLocaleString()}\nSubject: ${
    message.subject
  }\n\n${message.body || ""}`;
}

/**
 * Build initial recipients for reply mode
 */
export function buildInitialRecipients(
  isForwardMode: boolean,
  replyEmail?: string,
  replyMessage?: Message | null,
  replyingToMessage?: Message | null,
  modalCustomerName?: string,
  urlContactName?: string,
  propInitialRecipients?: EmailRecipient[]
): EmailRecipient[] | undefined {
  if (isForwardMode) return undefined;
  if (!replyEmail) return propInitialRecipients;

  return [
    {
      id: `email-${replyEmail}`,
      type: "email" as const,
      name:
        replyMessage?.replyTo?.[0]?.name ||
        replyingToMessage?.replyTo?.[0]?.name ||
        replyMessage?.sender?.name ||
        replyingToMessage?.sender?.name ||
        modalCustomerName ||
        urlContactName ||
        replyEmail,
      email: replyEmail,
    },
  ];
}

/**
 * Build follow-up task data from form submission
 */
export function buildFollowUpTaskData(
  values: ComposeFormValues,
  selectedClient: Client | null | undefined,
  recipients: EmailRecipient[],
  ccRecipients: EmailRecipient[],
  bccRecipients: EmailRecipient[],
  emailConnections: EmailConnection[],
  resolvedSubject: string,
  resolvedBody: string
): FollowUpTaskMessageData {
  const selectedConnection = emailConnections.find(
    (conn) => conn.connectionId === values.fromAccount
  );
  const fromAccountLabel = selectedConnection?.email || values.fromAccount || "";

  return {
    subject: resolvedSubject,
    body: resolvedBody,
    recipients: recipients.map((r) => r.email),
    ccRecipients: ccRecipients.map((r) => r.email),
    bccRecipients: bccRecipients.map((r) => r.email),
    fromAccount: values.fromAccount || "",
    fromAccountLabel,
    selectedClient: selectedClient
      ? {
          id: selectedClient.id,
          name: selectedClient.name || "",
          email: selectedClient.email || "",
        }
      : undefined,
  };
}

/**
 * Extract reply email from multiple sources with priority
 */
export function extractReplyEmail(
  isForwardMode: boolean,
  replyMessage?: Message | null,
  replyingToMessage?: Message | null,
  modalData?: any,
  urlContactData?: any,
  propInitialRecipient?: string
): string | undefined {
  if (isForwardMode) return undefined;

  // Reply-To wins over From: for mailing-list / "via" mail the From address is
  // the list itself (e.g. a group inbox), so replying to sender.email would send
  // back to the group. The RFC 5322 Reply-To is the intended reply target.
  return (
    replyMessage?.replyTo?.[0]?.email ||
    replyingToMessage?.replyTo?.[0]?.email ||
    replyMessage?.sender?.email ||
    replyingToMessage?.sender?.email ||
    modalData?.customerEmail ||
    urlContactData?.email ||
    propInitialRecipient ||
    undefined
  );
}

/**
 * Derive initial client from modal data or props
 */
export function deriveInitialClient(
  propInitialClient: Client | undefined,
  modalData?: any
): Client | undefined {
  if (propInitialClient) return propInitialClient;

  const item = modalData?.item;
  if (!item || typeof item !== "object") return undefined;

  const id = item.id ?? item.contact_id;
  const name = item.name ?? item.contact_name ?? "";
  const email = item.email ?? item.contact_email ?? modalData?.customerEmail ?? "";

  if (!id || !email) return undefined;

  return {
    id: String(id),
    name: String(name),
    email: String(email),
    company_name: item.company_name ?? item.companies?.name ?? undefined,
  } as Client;
}

/**
 * Build URL parameters for full-screen compose navigation
 */
export function buildComposeUrlParams(
  originalMessage?: Message | null,
  initialRecipient?: string,
  currentTo?: string,
  selectedClientId?: string
): URLSearchParams {
  const params = new URLSearchParams();

  if (originalMessage) {
    params.set("replyTo", originalMessage.id);
  } else if (initialRecipient) {
    params.set("to", initialRecipient);
  } else if (currentTo) {
    params.set("to", currentTo);
  }

  if (selectedClientId) {
    params.set("client", selectedClientId);
  }

  return params;
}

/**
 * Determine draft type based on context
 */
export function determineDraftType(
  originalMessage?: Message | null
): "reply" | "compose" {
  return originalMessage ? "reply" : "compose";
}
